import { useState } from 'react';
import {
  Button,
  DataTable,
  ErrorState,
  FilterBar,
  FilterDivider,
  FormField,
  Modal,
  Pager,
  Select,
  SelectableChip,
  StatusPill,
  Textarea,
  type DataTableColumn,
  type PillTone,
} from '../../components';
import { useNavigate } from '@tanstack/react-router';
import type { Capability } from '../../lib/auth/capabilities';
import { useSession } from '../../lib/auth/useSession';
import { formatDateTime } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { notify } from '../../services/notify';
import { numberParam, pickParam } from '../profiles/people';
import type { AlertDef, AlertEvent } from './api';
import { useAcknowledgeAlert, useAlertRegistry, useAlerts } from './queries';

const STATUSES = ['open', 'acknowledged'] as const;
/** Alert types owned outside Money, acknowledged with their own permission. */
const OWNED_ACK: Record<number, Capability> = { 9: 'alerts.bsc.ack', 14: 'alerts.campaign.ack' };
const SEVERITY_TONE: Record<string, PillTone> = { info: 'info', warn: 'warning', critical: 'danger' };

/** The campaign an alert is about (#14 carries it) — opening it is where Activate is. */
function campaignOf(a: AlertEvent): string | undefined {
  const id = a.triggerContext?.campaignId;
  return typeof id === 'string' && id ? id : undefined;
}

/** The trigger context as short "key: value" pairs. */
function contextLine(ctx: Record<string, unknown> | undefined): string {
  if (!ctx) return '';
  return Object.entries(ctx)
    .filter(([, v]) => v !== null && v !== '' && typeof v !== 'object')
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ');
}

function columns(
  registry: Map<number, AlertDef>,
  lockFor: (a: AlertEvent) => string | undefined,
  onAck: (a: AlertEvent) => void,
  onOpenCampaign: (id: string) => void,
): DataTableColumn<AlertEvent>[] {
  return [
    {
      id: 'severity',
      header: 'Severity',
      width: '110px',
      cell: (a) => {
        const sev = registry.get(a.alertCode)?.severity ?? 'info';
        return <StatusPill tone={SEVERITY_TONE[sev] ?? 'neutral'}>{sev}</StatusPill>;
      },
    },
    {
      id: 'alert',
      header: 'Alert',
      width: '2fr',
      wrap: true,
      cell: (a) => (
        <span className="min-w-0">
          <span className="block font-semibold">
            {registry.get(a.alertCode)?.name ?? `Alert #${a.alertCode}`}{' '}
            <span className="font-normal text-muted">#{a.alertCode}</span>
          </span>
          <span className="block truncate text-xs text-muted">{contextLine(a.triggerContext)}</span>
          {campaignOf(a) && (
            <button
              type="button"
              onClick={() => onOpenCampaign(campaignOf(a)!)}
              className="mt-0.5 cursor-pointer border-0 bg-transparent p-0 font-body text-xs font-semibold text-brown underline underline-offset-2 hover:text-orange"
            >
              Open campaign
            </button>
          )}
        </span>
      ),
    },
    {
      id: 'when',
      header: 'Triggered',
      width: '170px',
      cell: (a) => <span className="text-muted">{formatDateTime(a.triggeredAt)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.2fr',
      wrap: true,
      cell: (a) =>
        a.acknowledgedAt ? (
          <span className="text-[13px]">
            <StatusPill tone="success" dot>
              Acknowledged
            </StatusPill>
            {a.acknowledgeNote && <span className="mt-0.5 block text-xs text-muted">“{a.acknowledgeNote}”</span>}
          </span>
        ) : (
          <StatusPill tone="warning" dot>
            Open
          </StatusPill>
        ),
    },
    {
      id: 'act',
      header: '',
      width: '130px',
      align: 'right',
      action: true,
      cell: (a) =>
        !a.acknowledgedAt ? (
          <Button
            variant="ghost-dark"
            size="sm"
            lockedReason={lockFor(a)}
            onClick={(e) => {
              e.stopPropagation();
              onAck(a);
            }}
          >
            Acknowledge
          </Button>
        ) : null,
    },
  ];
}

/**
 * Alerts (§7.8): every alert type in the registry, open first. Acknowledging closes
 * an alert with an optional note; the next occurrence opens a new one.
 */
export function AlertsScreen() {
  const table = useTableUrlState();
  const { locked } = useSession();
  // #9 (Blue Star candidate) is CRM's and #14 (draft campaign traffic) Marketing's; every other type is Money's.
  const lockFor = (a: AlertEvent) => locked(OWNED_ACK[a.alertCode] ?? 'alerts.ack');
  const navigate = useNavigate();
  const openCampaign = (id: string) => void navigate({ to: `/campaigns/${id}` as never });
  const status = pickParam(table.filters.status, STATUSES);
  const code = numberParam(table.filters.code, 1, 99);
  const registry = useAlertRegistry();
  const byCode = new Map((registry.data ?? []).map((d) => [d.code, d]));
  const q = useAlerts({
    status,
    code,
    pageSize: table.pageSize,
    offset: (table.page - 1) * table.pageSize,
  });
  const [acking, setAcking] = useState<AlertEvent | null>(null);

  return (
    <div>
      <FilterBar>
        <SelectableChip active={!status} onClick={() => table.setFilter('status', undefined)}>
          All
        </SelectableChip>
        {STATUSES.map((s) => (
          <SelectableChip key={s} active={status === s} onClick={() => table.setFilter('status', s)}>
            {s === 'open' ? 'Open' : 'Acknowledged'}
          </SelectableChip>
        ))}
        <FilterDivider />
        <Select
          aria-label="Alert type"
          surface="white"
          compact
          style={{ width: 260 }}
          placeholder="Every alert type"
          value={code !== undefined ? String(code) : ''}
          options={(registry.data ?? []).map((d) => ({ value: String(d.code), label: `#${d.code} ${d.name}` }))}
          onChange={(e) => table.setFilter('code', e.target.value || undefined)}
        />
      </FilterBar>
      <DataTable
        label="Alerts"
        minWidth={1000}
        columns={columns(byCode, lockFor, setAcking, openCampaign)}
        rows={q.data?.items ?? []}
        getRowId={(a) => a.id}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No alerts match those filters."
      />
      <Pager
        page={table.page}
        pageSize={table.pageSize}
        total={q.data?.total ?? 0}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        noun="alerts"
      />
      {acking && <AckDialog alert={acking} name={byCode.get(acking.alertCode)?.name} onClose={() => setAcking(null)} />}
    </div>
  );
}

function AckDialog({ alert, name, onClose }: { alert: AlertEvent; name?: string; onClose: () => void }) {
  const ack = useAcknowledgeAlert();
  const [note, setNote] = useState('');
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={`Acknowledge ${name ?? `alert #${alert.alertCode}`}?`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={ack.isPending}
            onClick={() =>
              ack.mutate(
                { id: alert.id, note: note.trim() || undefined },
                {
                  onSuccess: () => {
                    notify.success('Alert acknowledged.');
                    onClose();
                  },
                },
              )
            }
          >
            Acknowledge
          </Button>
        </>
      }
    >
      <p className="mt-0 text-sm text-muted">{contextLine(alert.triggerContext) || 'No further detail.'}</p>
      <FormField label="Note" htmlFor="ack-note" optional hint="What was done about it.">
        <Textarea id="ack-note" surface="white" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </FormField>
      {ack.error && <ErrorState error={ack.error} compact />}
    </Modal>
  );
}
